import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LayoutDashboard, BookOpen, Settings, Bell, Search, Plus,
    Upload, FileText, FileImage, Trash2, Calendar, Target,
    X, Users, Trophy, MessageSquare, Briefcase,
    ChevronRight, ChevronLeft, Menu, FolderOpen,
    HelpCircle, Heart, Award, Zap, LogOut,
    Globe, Lock, Link as LinkIcon, Folder, SlidersHorizontal,
    AlertCircle, Check
} from 'lucide-react';
import { useLang } from '../hooks/useLang';
import { useAuthContext } from '../hooks/useAuthContext';
// import { useLogout } from '../hooks/useLogout'; // Removed unused
// import { useTheme } from '../context/ThemeContext'; // Removed unused
import '../styles/pages/User.scss';

import DashboardContent from '../components/DashboardContent';
import MyNotes from '../components/MyNotes';
import Library from './Library';
import CategoriesPage from '../components/CategoriesPage';
// import AddCategory from '../components/AddCategory'; // Removed unused
import OpportunitiesContent from '../components/opportunities/OpportunitiesContent';
import Navbar from '../components/Navbar';
import UploadPanel from '../components/pdf/UploadPanel';
import AiTools from '../components/AiTools';
import AnalyticsContent from '../components/AnalyticsContent';
import CommunityContent from '../components/CommunityContent';
import { Toaster } from '../components/ui/toaster';

const User = () => {
    const { user, token } = useAuthContext();
    const location = useLocation();
    // const { language } = useLang(); // Removed unused
    
    // Get tab directly from URL - e.g. /en/notes -> notes
    const activeTab = location.pathname.split('/')[2] || 'dashboard';

    // Shared State for materials and collections
    const [collections, setCollections] = useState([]);
    const [activeFolderId, setActiveFolderId] = useState(null);
    // const [materials, setMaterials] = useState({ uploaded: [], saved: [], favorites: [] }); // Removed unused
    const [cvs, setCvs] = useState([]);

    const scrollContainerRef = useRef(null);

    // Auto-scroll to top when tab changes
    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [activeTab]);

    const fetchCollections = useCallback(async () => {
        try {
            const res = await fetch('/api/folders', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCollections(data);
            }
        } catch (err) {
            console.error(err);
        }
    }, [token]);

    const fetchMaterials = useCallback(async () => {
        try {
            const res = await fetch('/api/materials/my-books', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                // setMaterials(data); // Removed unused
            }
        } catch (err) {
            console.error(err);
        }
    }, [token]);

    const fetchCvs = useCallback(async () => {
        try {
            const res = await fetch('/api/cvs', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setCvs(data.cvs || []);
            }
        } catch (err) {
            console.error(err);
        }
    }, [token]);

    useEffect(() => {
        if (user && token) {
            fetchCollections();
            fetchMaterials();
            fetchCvs();
        }
    }, [user, token, fetchCollections, fetchMaterials, fetchCvs]);

    // Upload & Metadata State
    const [stagedFiles, setStagedFiles] = useState([]);
    const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
    const [uploadType, setUploadType] = useState('material'); // 'material', 'latex', or 'cv'
    const materialInputRef = useRef(null);
    const latexInputRef = useRef(null);
    const cvInputRef = useRef(null);

    const [uploadStatus, setUploadStatus] = useState({ type: null, message: '' }); // { type: 'success' | 'error' | 'loading', message: '' }

    const handleFileSelect = async (e, forceType = null) => {
        const filesArray = Array.from(e.target.files);
        if (filesArray.length === 0) return;

        const currentType = forceType || uploadType;

        // Strict filter for LaTeX
        const filtered = currentType === 'latex' 
            ? filesArray.filter(f => 
                f.name.trim().toLowerCase().endsWith('.tex') || 
                f.type.includes('tex') ||
                f.type === 'text/plain'
            )
            : filesArray;

        if (currentType === 'latex' && filtered.length === 0) {
            setUploadStatus({ type: 'error', message: 'Only .tex files allowed for LaTeX mode.' });
            setTimeout(() => setUploadStatus({ type: null, message: '' }), 4000);
            e.target.value = '';
            return;
        }
        
        if (currentType === 'cv') {
            await directUploadCv(filesArray[0]);
        } else {
            const newStaged = (currentType === 'latex' ? filtered.slice(0, 1) : filtered).map(f => ({
                id: Math.random().toString(36).substr(2, 9),
                file: f,
                name: f.name,
                previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null
            }));
            setStagedFiles(newStaged);
            setIsUploadPanelOpen(true);
        }
        // Reset input
        e.target.value = '';
    };

    const directUploadCv = async (file) => {
        setUploadStatus({ type: 'loading', message: 'Uploading CV...' });
        const formData = new FormData();
        formData.append('cv', file);

        try {
            const res = await fetch('/api/cvs', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });
            if (res.ok) {
                await res.json();
                setUploadStatus({ type: 'success', message: 'CV Uploaded Successfully' });
                fetchCvs();
                setTimeout(() => setUploadStatus({ type: null, message: '' }), 3000);
            } else {
                let errorMsg = 'Upload failed';
                try {
                    const data = await res.json();
                    errorMsg = data.message || data.error || errorMsg;
                } catch (e) { /* fallback if not json */ }
                setUploadStatus({ type: 'error', message: errorMsg });
                setTimeout(() => setUploadStatus({ type: null, message: '' }), 4000);
            }
        } catch (err) {
            console.error(err);
            setUploadStatus({ type: 'error', message: 'Network error' });
            setTimeout(() => setUploadStatus({ type: null, message: '' }), 4000);
        } finally {
        }
    };

    const deleteCv = async (id) => {
        const cvToDelete = cvs.find(c => c.id === id);
        const wasDefault = cvToDelete?.isDefault;

        setUploadStatus({ type: 'loading', message: 'Deleting CV...' });
        try {
            const res = await fetch(`/api/cvs/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setUploadStatus({ type: 'success', message: 'CV Deleted' });
                
                // If the deleted CV was default, set another one as default
                if (wasDefault && cvs.length > 1) {
                    const nextCv = cvs.find(c => c.id !== id);
                    if (nextCv) {
                        await setDefaultCv(nextCv.id);
                    }
                } else {
                    fetchCvs();
                }
                
                setTimeout(() => setUploadStatus({ type: null, message: '' }), 3000);
            } else {
                setUploadStatus({ type: 'error', message: 'Delete failed' });
                setTimeout(() => setUploadStatus({ type: null, message: '' }), 4000);
            }
        } catch (err) {
            console.error(err);
            setUploadStatus({ type: 'error', message: 'Network error' });
            setTimeout(() => setUploadStatus({ type: null, message: '' }), 4000);
        }
    };

    const setDefaultCv = async (id) => {
        setUploadStatus({ type: 'loading', message: 'Updating default...' });
        try {
            const res = await fetch(`/api/cvs/${id}/default`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setUploadStatus({ type: 'success', message: 'Default CV Updated' });
                fetchCvs();
                setTimeout(() => setUploadStatus({ type: null, message: '' }), 3000);
            } else {
                setUploadStatus({ type: 'error', message: 'Update failed' });
                setTimeout(() => setUploadStatus({ type: null, message: '' }), 4000);
            }
        } catch (err) {
            console.error(err);
            setUploadStatus({ type: 'error', message: 'Network error' });
            setTimeout(() => setUploadStatus({ type: null, message: '' }), 4000);
        }
    };

    const triggerUpload = (type = 'material') => {
        if (type === 'unified') {
            if (uploadType !== 'material' && uploadType !== 'latex') {
                setStagedFiles([]);
            }
            if (uploadType === 'cv') setUploadType('material');
            setIsUploadPanelOpen(true);
            return;
        }

        if (type !== uploadType) {
            setStagedFiles([]);
        }
        setUploadType(type);
        
        if (type === 'latex' || type === 'material') {
            setIsUploadPanelOpen(true);
        } else {
            if (type === 'cv' && cvInputRef.current) cvInputRef.current.click();
        }
    };

    return (
        <div className="user-dashboard-dark flex flex-col min-h-screen bg-background text-foreground">
            <Navbar />

            <div className="flex-1 flex flex-col relative overflow-hidden">
                <main 
                    className="flex-1 overflow-y-auto scrollbar-hide" 
                    ref={scrollContainerRef}
                >
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.15, ease: 'easeOut' }}
                        >
                            {activeTab === 'dashboard' && (
                                <DashboardContent onUploadClick={triggerUpload} />
                            )}

                            {activeTab === 'analytics' && (
                                <AnalyticsContent />
                            )}

                            {activeTab === 'notes' && (
                                <MyNotes
                                    collections={collections}
                                    fetchCollections={fetchCollections}
                                    activeFolderId={activeFolderId}
                                    setActiveFolderId={setActiveFolderId}
                                    onUploadClick={() => triggerUpload('material')}
                                />
                            )}

                            {activeTab === 'library' && (
                                <Library scrollContainerRef={scrollContainerRef} />
                            )}

                            {(activeTab === 'categories' || activeTab === 'add-category') && (
                                <CategoriesPage user={user} token={token} />
                            )}

                            {activeTab === 'opportunities' && (
                                <OpportunitiesContent
                                    user={user}
                                    cvs={cvs}
                                    fetchCvs={fetchCvs}
                                    onUploadCv={() => triggerUpload('cv')}
                                    onDeleteCv={deleteCv}
                                    onSetDefaultCv={setDefaultCv}
                                />
                            )}

                            {activeTab === 'community' && (
                                <CommunityContent />
                            )}

                            {activeTab === 'ai' && <AiTools />}
                        </motion.div>
                    </AnimatePresence>
                    {uploadStatus.type && (
                        <div className="fixed top-6 right-6 z-[100] animate-in fade-in slide-in-from-top-4 duration-300">
                            <div className={`
                                flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl border backdrop-blur-md
                                ${uploadStatus.type === 'loading' ? 'bg-[#0a2022] border-[#1a3032] text-white' : ''}
                                ${uploadStatus.type === 'success' ? 'bg-[#0d2a21] border-[#1b4332] text-[#4ade80]' : ''}
                                ${uploadStatus.type === 'error' ? 'bg-[#2a0d0d] border-[#431b1b] text-[#f87171]' : ''}
                            `}>
                                {uploadStatus.type === 'loading' && (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                )}
                                {uploadStatus.type === 'success' && <Check className="w-4 h-4" />}
                                {uploadStatus.type === 'error' && <AlertCircle className="w-4 h-4" />}
                                <span className="text-sm font-bold tracking-tight">{uploadStatus.message}</span>
                            </div>
                        </div>
                    )}
                </main>

                <AnimatePresence>
                    {isUploadPanelOpen && (
                        <UploadPanel 
                            stagedFiles={stagedFiles}
                            setStagedFiles={setStagedFiles}
                            setIsOpen={setIsUploadPanelOpen}
                            token={token}
                            uploadType={uploadType}
                            setUploadType={setUploadType}
                            onUploadSuccess={() => {
                                fetchMaterials();
                                fetchCvs();
                            }}
                        />
                    )}
                </AnimatePresence>
            </div>
            
            <Toaster />
            
            {/* Specialized hidden inputs to avoid race conditions and ensure strict 'accept' filters */}
            <input type="file" multiple ref={materialInputRef} style={{ display: 'none' }} onChange={(e) => handleFileSelect(e, 'material')} accept=".pdf,application/pdf,image/*" />
            <input type="file" ref={latexInputRef} style={{ display: 'none' }} onChange={(e) => handleFileSelect(e, 'latex')} accept=".tex,text/plain,text/x-tex" />
            <input type="file" ref={cvInputRef} style={{ display: 'none' }} onChange={(e) => handleFileSelect(e, 'cv')} accept=".pdf,application/pdf" />
        </div>
    );
};

export default User;
